/**
 * @fileoverview Component for displaying a list of studies.
 * This component includes a header with a search bar and a table of studies.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetStudiesCountQuery, useGetStudiesQuery } from '../../services/studies'
import { Search } from '../elements/Search'
import { Pagination } from '../Pagination'
import { StudyListEmpty } from './StudyListEmpty'
import { StudyListTable } from './StudyListTable'

/**
 * @function StudyList
 * @description Component for displaying a list of studies.
 * @returns {JSX.Element} - A React component that renders the list of studies.
 */
export function StudyList() {
    const navigate = useNavigate()

    const {
        data: studies,
        isLoading,
        refetch: refetchStudies,
    } = useGetStudiesQuery(new URLSearchParams(location.search).toString())
    const { data: total, refetch: refetchStudiesCount } = useGetStudiesCountQuery()

    useEffect(() => {
        refetchStudies()
        refetchStudiesCount()
    }, [location.search])

    function handleRowClick(e: Event, userId: string) {
        if (e.target instanceof HTMLInputElement) {
            return
        }
        navigate(userId + location.search)
    }

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex flex-wrap fixed top-0 left-[256px] right-0 p-8 bg-white z-20 border-b justify-between">
                <div className="flex flex-col gap-y-6">
                    <div className="flex items-center h-full">
                        <span className="text-2xl font-semibold mr-3">Studies</span>
                    </div>
                    <Search placeholder="Search by study name…" />
                </div>
            </div>
            <div className="relative w-full mt-[161px]">
                <div className="flex flex-wrap bg-white">
                    <div className="w-full min-h-[calc(100vh_-_204px)] flex flex-wrap">
                        <div className="inline-block min-w-full align-middle">
                            <div className="flex flex-col">
                                {total?.count > 0 ? (
                                    <>
                                        <StudyListTable
                                            data={studies}
                                            onRowClicked={handleRowClick}
                                        />
                                        <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                                            <Pagination total={total?.count} />
                                        </div>
                                    </>
                                ) : (
                                    <StudyListEmpty />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
